import { v5 as uuidv5 } from "uuid";
import {
  httpDelete,
  httpGet,
  httpPost,
  httpPut,
} from "../../../../../../Utils/apis/apis";
import { handleOptionSetValue } from "../../../../../../Utils/Helpers/optionSet.helper";
import { message } from "../../../../../../Utils/config/messages";

export const BORROWER_STEPS = [
  {
    title: "Identity",
    fields: [
      {
        backendKey: "BorrowerType",
        label: "Borrower Type",
        type: "enum",
        required: true,
      },
      {
        backendKey: "LegalName",
        label: "Legal Name",
        type: "string",
        required: true,
      },
      {
        backendKey: "TradingName",
        label: "Trading Name",
        type: "string",
      },
      {
        backendKey: "CompanyNumber",
        label: "Company House Number",
        type: "string",
      },
      {
        backendKey: "IncorporationDate",
        label: "Incorporation Date",
        type: "date",
      },
      {
        backendKey: "DateOfBirth",
        label: "Date of Birth",
        type: "date",
      },
      {
        backendKey: "ExposureGroupName",
        label: "Exposure Group",
        type: "exposure-group-search",
        required: false,
      },
      {
        backendKey: "NumberOfEmployees",
        label: "Number of Employees",
        type: "int"
      },
      {
        backendKey: "TotalTurnover",
        label: "Total Turnover",
        type: "decimal"
      },
    ],
  },
  {
    title: "Address",
    sections: [
      {
        title: "Registered Address",
        fields: [
          {
            backendKey: "RegisteredAddressLine1",
            label: "Address Line 1",
            type: "string",
            required: true,
          },
          {
            backendKey: "RegisteredAddressLine2",
            label: "Address Line 2",
            type: "string",
            required: true,
          },
          {
            backendKey: "RegisteredAddressTownName",
            label: "Town Name",
            type: "string",
          },
          {
            backendKey: "Postcode",
            label: "Postcode",
            type: "string",
            required: true,
          },
          {
            backendKey: "Country",
            label: "Country",
            type: "lookup",
            required: true,
          },
        ],
      },
      {
        title: "Operating Address",
        fields: [
          {
            backendKey: "OperatingAddressLine1",
            label: "Address Line 1",
            type: "string",
          },
          {
            backendKey: "OperatingAddressLine2",
            label: "Address Line 2",
            type: "string",
          },
          {
            backendKey: "OperatingAddressTownName",
            label: "Town Name",
            type: "string",
          },
          {
            backendKey: "OperatingAddressPostcode",
            label: "Postcode",
            type: "string",
            required: false,
          },
          {
            backendKey: "OperatingAddressCountry",
            label: "Country",
            type: "lookup",
            required: false,
          },
        ],
      },
    ],
  },
  {
    title: "Compliance",
    fields: [
      {
        backendKey: "SicCodes",
        label: "SIC Codes",
        type: "string",
      },
      {
        backendKey: "VatNumber",
        label: "VAT Number",
        type: "string",
        placeholder: "e.g. GB123456789",
        fieldHelperText:
          "UK VAT number: starts with GB followed by digits only (e.g. GB123456789).",
      },
      {
        backendKey: "UkTaxResident",
        label: "UK Tax Resident",
        type: "boolean",
      },
      {
        backendKey: "MarketingOptIn",
        label: "Marketing Opt-In",
        type: "boolean",
      },
    ],
  },
  {
    title: "Contact",
    fields: [
      {
        backendKey: "ContactName",
        label: "Primary Contact Name",
        type: "string",
      },
      {
        backendKey: "ContactEmail",
        label: "Primary Contact Email",
        type: "string",
      },
      {
        backendKey: "ContactPhone",
        label: "Primary Contact Phone",
        type: "string",
      },
      {
        backendKey: "PreferredContactMethod",
        label: "Preferred Contact Method",
        type: "enum",
      },
    ],
  },
];

export const BOOLEAN_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

export const REGISTERED_TO_OPERATING_ADDRESS_MAP = {
  RegisteredAddressLine1: "OperatingAddressLine1",
  RegisteredAddressLine2: "OperatingAddressLine2",
  RegisteredAddressTownName: "OperatingAddressTownName",
  Postcode: "OperatingAddressPostcode",
  Country: "OperatingAddressCountry",
};

export const LEGACY_KEY_MAP = {
  borrower_type: "BorrowerType",
  legal_name: "LegalName",
  trading_name: "TradingName",
  exposuregroupname: "ExposureGroupName",
  company_number: "CompanyNumber",
  registered_address: "Address",
  operating_address: "OperatingAddress",
  incorporation_date: "IncorporationDate",
  number_of_employees: "NumberOfEmployees",
  total_turnover: "TotalTurnover",
  sic_codes: "SicCodes",
  vat_number: "VatNumber",
  postcode: "Postcode",
  country: "Country",
  operating_postcode: "OperatingPostcode",
  operating_country: "OperatingCountry",
  registered_address_street_name: "RegisteredAddressLine1",
  registered_address_building_number: "RegisteredAddressLine2",
  registered_address_town_name: "RegisteredAddressTownName",
  operating_address_street_name: "OperatingAddressLine1",
  operating_address_building_number: "OperatingAddressLine2",
  operating_address_town_name: "OperatingAddressTownName",
  date_of_birth: "DateOfBirth",
  contact_name: "ContactName",
  contact_email: "ContactEmail",
  contact_phone: "ContactPhone",
  preferred_contact_method: "PreferredContactMethod",
  uk_tax_resident: "UkTaxResident",
  marketing_opt_in: "MarketingOptIn",
};

const ADD_BORROWER_DRAFT_STORAGE_PREFIX = "minerva:add-borrower:draft-form-id";
const FORM_DRAFT_NAMESPACE = "20c91c1f-7f6f-462b-9f75-f028d5a22a87";
const FORM_DRAFT_ENDPOINT = `${process.env.API_URL || ""}`
  .replace(/\/+$/, "")
  .toLowerCase()
  .endsWith("/api")
  ? "/formdraft"
  : "/api/formdraft";

const getFieldsFromStep = (step) => {
  if (step.sections) {
    return step.sections.flatMap((section) => section.fields || []);
  }

  return step.fields || [];
};

export const ALL_FIELDS = BORROWER_STEPS.flatMap((step) =>
  getFieldsFromStep(step)
);

export const buildInitialState = () =>
  ALL_FIELDS.reduce((state, field) => {
    state[field.backendKey] = "";
    return state;
  }, {});

const hasOwnValue = (value) =>
  value !== "" && value !== null && value !== undefined;

const normalizeDraftIdentifier = (value = "anonymous") => {
  const normalizedValue = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalizedValue || "anonymous";
};

const resolveBorrowerDraftFormId = (draftIdentifier) =>
  draftIdentifier
    ? uuidv5(
        `${ADD_BORROWER_DRAFT_STORAGE_PREFIX}:${draftIdentifier}`,
        FORM_DRAFT_NAMESPACE
      )
    : null;

export const getBorrowerDraftStorageKey = (userIdentifier = "anonymous") => {
  const hostName =
    typeof window !== "undefined"
      ? window.location?.hostname || "portal"
      : "portal";

  return [
    ADD_BORROWER_DRAFT_STORAGE_PREFIX,
    normalizeDraftIdentifier(hostName),
    normalizeDraftIdentifier(userIdentifier),
  ].join(":");
};

export const hasDraftableBorrowerData = (
  formData = {},
  activeStep = 0,
  isOperatingAddressSameAsRegistered = false
) => {
  const initialState = buildInitialState();
  const hasFormValues = ALL_FIELDS.some(
    (field) =>
      `${formData[field.backendKey] ?? ""}` !==
      `${initialState[field.backendKey] ?? ""}`
  );

  return hasFormValues || activeStep > 0 || isOperatingAddressSameAsRegistered;
};

export const saveBorrowerDraft = async (storageKey, payload) => {
  const formId = resolveBorrowerDraftFormId(storageKey);

  if (!formId) {
    return false;
  }

  try {
    let response = await httpPut(
      `${FORM_DRAFT_ENDPOINT}/${encodeURIComponent(formId)}`,
      {
        formId,
        content: JSON.stringify(payload),
      }
    );

    if (response?.response?.status === 404 || response?.status === 404) {
      response = await httpPost(FORM_DRAFT_ENDPOINT, {
        formId,
        content: JSON.stringify(payload),
      });
    }

    if (
      response?.response?.status >= 400 ||
      response?.status >= 400 ||
      response?.isAxiosError
    ) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const loadBorrowerDraft = async (storageKey) => {
  const formId = resolveBorrowerDraftFormId(storageKey);

  if (!formId) {
    return null;
  }

  try {
    const response = await httpGet(
      `${FORM_DRAFT_ENDPOINT}?formId=${encodeURIComponent(formId)}`
    );

    if (response?.response?.status === 404 || response?.status === 404) {
      return null;
    }

    if (!response?.content) {
      return null;
    }

    return JSON.parse(response.content);
  } catch (error) {
    return null;
  }
};

export const clearBorrowerDraft = async (storageKey) => {
  const formId = resolveBorrowerDraftFormId(storageKey);

  if (!formId) {
    return;
  }

  try {
    await httpDelete(`${FORM_DRAFT_ENDPOINT}/${encodeURIComponent(formId)}`);
  } catch (error) {
    // ignore API delete failure so the form can still be reset in the UI
  }
};

export const sanitizeBorrowerDraft = (draft) => {
  if (!draft || typeof draft !== "object") {
    return null;
  }

  const nextFormData = buildInitialState();

  ALL_FIELDS.forEach((field) => {
    nextFormData[field.backendKey] = hasOwnValue(
      draft?.formData?.[field.backendKey]
    )
      ? draft.formData[field.backendKey]
      : "";
  });

  return {
    activeStep:
      typeof draft.activeStep === "number" && draft.activeStep >= 0
        ? draft.activeStep
        : 0,
    formData: nextFormData,
    isOperatingAddressSameAsRegistered:
      !!draft.isOperatingAddressSameAsRegistered,
    updatedAt: draft.updatedAt || null,
  };
};

export const getRecordSource = (formConfig) =>
  formConfig?.data ||
  formConfig?.record ||
  formConfig?.recordData ||
  formConfig?.properties ||
  formConfig?.templateData ||
  formConfig?.[0] ||
  formConfig ||
  {};

export const toOptionLabel = (value) =>
  `${value}`.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export const copyRegisteredAddressToOperating = (sourceState) =>
  Object.entries(REGISTERED_TO_OPERATING_ADDRESS_MAP).reduce(
    (nextState, [registeredKey, operatingKey]) => {
      nextState[operatingKey] = sourceState[registeredKey] || "";
      return nextState;
    },
    { ...sourceState }
  );

export const buildBorrowerStateFromSource = (source) =>
  ALL_FIELDS.reduce((state, field) => {
    const legacyKey = Object.keys(LEGACY_KEY_MAP).find(
      (key) => LEGACY_KEY_MAP[key] === field.backendKey
    );
    const rawValue = source?.[field.backendKey] ?? source?.[legacyKey] ?? "";
    state[field.backendKey] =
      (field.type === "enum" || field.type === "lookup") && rawValue
        ? handleOptionSetValue(rawValue, false)
        : rawValue;
    return state;
  }, {});

export const isOperatingAddressSynced = (formData) => {
  const hasAddressValue = Object.keys(REGISTERED_TO_OPERATING_ADDRESS_MAP).some(
    (registeredKey) => formData[registeredKey]
  );

  return (
    hasAddressValue &&
    Object.entries(REGISTERED_TO_OPERATING_ADDRESS_MAP).every(
      ([registeredKey, operatingKey]) =>
        (formData[registeredKey] || "") === (formData[operatingKey] || "")
    )
  );
};

export const buildBorrowerOptionSetObject = (categories, data = []) =>
  categories.reduce((acc, category) => {
    const items = data
      .filter((x) => x.category === category)
      .map(({ id, label, value }) => ({
        id,
        label,
        value,
      }));

    acc[category] = items.length ? items : null;
    return acc;
  }, {});

export const NON_INDIVIDUAL_ONLY_FIELD_KEYS = [
  "CompanyNumber",
  "IncorporationDate",
  "NumberOfEmployees",
  "TotalTurnover",
  "SicCodes",
  "VatNumber",
];

export const INDIVIDUAL_ONLY_FIELD_KEYS = ["DateOfBirth"];

export const isNumericBorrowerFieldType = (type) =>
  type === "int" || type === "decimal";

export const shouldRenderBorrowerField = (
  field,
  isIndividual,
  isNonIndividual
) => {
  if (NON_INDIVIDUAL_ONLY_FIELD_KEYS.includes(field.backendKey)) {
    return isNonIndividual;
  }

  if (INDIVIDUAL_ONLY_FIELD_KEYS.includes(field.backendKey)) {
    return isIndividual;
  }

  return true;
};

export const isBorrowerFieldRequired = (
  field,
  isIndividual,
  isNonIndividual
) => {
  if (NON_INDIVIDUAL_ONLY_FIELD_KEYS.includes(field.backendKey)) {
    return isNonIndividual;
  }

  if (INDIVIDUAL_ONLY_FIELD_KEYS.includes(field.backendKey)) {
    return isIndividual;
  }

  return field.required;
};

export const validateBorrowerStep = ({
  currentStep,
  formData,
  isIndividual,
  isNonIndividual,
}) => {
  const newErrors = {};
  const fieldsToValidate = currentStep.sections
    ? currentStep.sections.flatMap((section) => section.fields)
    : currentStep.fields || [];

  fieldsToValidate
    .filter((field) =>
      shouldRenderBorrowerField(field, isIndividual, isNonIndividual)
    )
    .forEach((field) => {
      const value = formData[field.backendKey];

      if (isNumericBorrowerFieldType(field.type) && `${value}`.trim() !== "") {
        const numericText = `${value}`.trim();

        if (field.type === "int" && !/^-?\d+$/.test(numericText)) {
          newErrors[field.backendKey] = message.ENTER_WHOLE_NUMBER;
        } else if (Number.isNaN(Number(numericText))) {
          newErrors[field.backendKey] = message.ENTER_VALID_NUMBER;
        } else if (Number(numericText) < 0) {
          newErrors[field.backendKey] = message.VALUE_MIN_ZERO;
        }
      }

      if (field.backendKey === "VatNumber" && value) {
        const vatRegex = /^GB\d*$/;
        if (!vatRegex.test(value)) {
          newErrors[field.backendKey] =
            "VAT Number must start with GB and contain only digits after";
        }
      }

      if (
        isBorrowerFieldRequired(field, isIndividual, isNonIndividual) &&
        (!value || value === "")
      ) {
        newErrors[field.backendKey] = `${field.label} is required`;
      }
    });

  return newErrors;
};

export const parseBorrowerApiErrors = (errorData) => {
  const apiErrors = {};

  if (errorData?.errors && typeof errorData.errors === "object") {
    Object.entries(errorData.errors).forEach(([fieldKey, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        apiErrors[fieldKey] = messages[0];
      }
    });
  }

  return apiErrors;
};

export const ExecuteSqlStoreProc = async (payload) => {
  const response = await httpPost(
    "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
    payload
  );

  return response;
};
